import React from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Home, FileText } from 'lucide-react';

/**
 * 404 page, matching maxelpay.com's (app/not-found.js + not-found.module.css
 * and the globals it uses: .aurora, .grid-bg, .gradient-text, .btn).
 *
 * Styles are self-contained and prefixed `nf-` so nothing leaks into, or is
 * overridden by, the dashboard's stylesheet. Values are copied from the site's
 * design tokens rather than referenced, because this app does not load them.
 */
const NotFound = () => {
  const { isAuthenticated } = useSelector((state) => state.auth);
  // "Home" is the dashboard for a signed-in merchant and the login page for
  // anyone else -- the bare "/" route only redirects to /login.
  const homePath = isAuthenticated ? '/dashboard' : '/login';

  return (
    <section className="nf-wrap">
      <div className="nf-aurora" aria-hidden="true" />
      <div className="nf-grid" aria-hidden="true" />
      <div className="nf-inner">
        <div className="nf-code" aria-hidden="true">404</div>
        <h1 className="nf-h1">This page went off-chain</h1>
        <p className="nf-sub">
          The page you were looking for doesn’t exist, moved, or never settled.
          Let’s get you back to somewhere that does.
        </p>
        <div className="nf-ctas">
          <Link to={homePath} className="nf-btn nf-btn-primary">
            <Home size={16} /> Back to home
          </Link>
          <a
            href="https://docs.maxelpay.com/"
            className="nf-btn nf-btn-outline"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FileText size={16} /> Read the docs
          </a>
        </div>
      </div>
      <style>{styles}</style>
    </section>
  );
};

const styles = `
  .nf-wrap {
    /* maxelpay.com tokens */
    --nf-display: "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
    --nf-body: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
    --nf-muted: #9A99AD;
    --nf-line: rgba(255, 255, 255, 0.08);
    --nf-brand: #7C6CFF;
    --nf-brand-2: #A855F7;

    position: relative;
    overflow: hidden;
    min-height: 100vh;
    display: grid;
    place-items: center;
    padding: 90px 24px;
    background: #ffffff;
    font-family: var(--nf-body);
    line-height: 1.55;
    -webkit-font-smoothing: antialiased;
    box-sizing: border-box;
  }

  /* Violet glow behind the 404 (site .aurora). */
  .nf-aurora {
    position: absolute; inset: 0; z-index: 0; pointer-events: none; overflow: hidden;
  }
  .nf-aurora::before {
    content: "";
    position: absolute;
    top: -40%; left: 50%;
    width: 1100px; height: 760px;
    transform: translateX(-50%);
    background: radial-gradient(ellipse 50% 50% at 50% 50%, rgba(124, 108, 255, 0.4), rgba(168, 85, 247, 0.14) 42%, transparent 68%);
    filter: blur(10px);
  }

  /* Faint grid fading out from the top centre (site .grid-bg). */
  .nf-grid {
    position: absolute; inset: 0; z-index: 0; opacity: 0.5; pointer-events: none;
    background-image: linear-gradient(var(--nf-line) 1px, transparent 1px), linear-gradient(90deg, var(--nf-line) 1px, transparent 1px);
    background-size: 56px 56px;
    -webkit-mask-image: radial-gradient(circle at 50% 16%, #000 0%, transparent 60%);
    mask-image: radial-gradient(circle at 50% 16%, #000 0%, transparent 60%);
  }

  .nf-inner {
    position: relative;
    z-index: 1;
    max-width: 560px;
    margin: 0 auto;
    text-align: center;
  }

  .nf-code {
    font-family: var(--nf-display);
    font-weight: 700;
    font-size: clamp(96px, 22vw, 200px);
    line-height: 0.92;
    letter-spacing: -0.04em;
    margin: 18px 0 6px;
    font-variant-numeric: tabular-nums;
    /* site .gradient-text */
    background: linear-gradient(120deg, #8051d0, #9F73AB);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }

  .nf-h1 {
    font-family: var(--nf-display);
    font-weight: 700;
    letter-spacing: -0.025em;
    text-wrap: balance;
    color: #432b7a;
    font-size: clamp(26px, 4vw, 40px);
    line-height: 1.1;
    margin: 0;
  }

  .nf-sub {
    color: var(--nf-muted);
    font-size: clamp(15px, 2vw, 18px);
    margin: 18px auto 0;
    max-width: 440px;
  }

  .nf-ctas {
    display: flex;
    gap: 12px;
    justify-content: center;
    margin-top: 32px;
    flex-wrap: wrap;
  }

  /* site .btn */
  .nf-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    justify-content: center;
    height: 46px;
    padding: 0 22px;
    border-radius: 999px;
    font-family: var(--nf-body);
    font-weight: 600;
    font-size: 14.5px;
    text-decoration: none;
    cursor: pointer;
    box-sizing: border-box;
    transition: transform 0.15s ease, background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
  }
  .nf-btn:hover { transform: translateY(-1px); }
  .nf-btn:focus-visible { outline: 2px solid var(--nf-brand); outline-offset: 2px; }

  /* site .btn-primary */
  .nf-btn-primary {
    border: none;
    color: #ffffff;
    font-weight: 700;
    background: linear-gradient(120deg, var(--nf-brand), var(--nf-brand-2));
    box-shadow: 0 8px 30px -10px rgba(124, 108, 255, 0.7);
  }
  .nf-btn-primary:hover { box-shadow: 0 12px 36px -8px rgba(124, 108, 255, 0.9); }

  /* site a.btn.border-ppl */
  .nf-btn-outline {
    color: #7646e1;
    border: 1px solid #7646e1;
    background: transparent;
  }

  @media (max-width: 900px) {
    .nf-wrap { padding: 64px 16px; }
  }
`;

export default NotFound;
